import React, { ReactElement, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DocumentNode } from 'graphql';
import {
  WatchQueryFetchPolicy,
  Context,
  OperationVariables,
  useQuery,
  NetworkStatus,
  ApolloQueryResult,
  ApolloError,
} from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import { useIntl } from 'react-intl';
import Button from '@fuww/library/src/Button';
import ReplayIcon from '@fuww/library/src/Icons/Replay';
import ErrorWrapper from '@fuww/library/src/ErrorWrapper';
import Loader from '../components/Loader';
import Error from '../components/Error';
import messages from './messages.json';

export const getLoading = (
  clientLoading: boolean,
  setLoadingOnPoll: boolean,
  networkStatus : NetworkStatus,
) => (
  typeof window === 'undefined'
    ? true
    : (!setLoadingOnPoll && networkStatus === NetworkStatus.poll
      ? false : clientLoading)
);

const wrappedComponentPropertyTypes = {
  query: PropTypes.shape({}).isRequired,
  variables: PropTypes.shape({}),
  context: PropTypes.shape({}),
  errorMessage: PropTypes.string.isRequired,
  loader: PropTypes.element,
  showLoaderWhenSetVariables: PropTypes.bool,
  showLoaderWhenDataIsEmpty: PropTypes.bool,
  skip: PropTypes.bool,
  ssr: PropTypes.bool,
  passErrors: PropTypes.arrayOf(PropTypes.string),
  fetchPolicy: PropTypes.string,
  notifyOnNetworkStatusChange: PropTypes.bool,
  showLoader: PropTypes.bool,
};

const withQuery = <
  Q,
  V extends OperationVariables,
  R extends boolean | undefined = false,
  P extends Record<string, unknown> = Record<string, unknown>,
>(
    Component: React.ComponentType<P & {
      data: R extends true ? Q | undefined : Q;
      loading: boolean;
      fetchMore: ReturnType<typeof useQuery<Q, V>>['fetchMore'];
      variables: V;
      error?: ApolloError;
      refetch: (
        variables?: Partial<V> | undefined
      ) => Promise<ApolloQueryResult<Q>>;
      skip: boolean;
      showLoader: boolean;
    }>,
    {
      renderAlways,
    }: {
      renderAlways?: R;
    } = {},
  ) => {
  const WrappedComponent = ({
    query,
    variables,
    errorMessage,
    context,
    loader,
    showLoaderWhenSetVariables,
    showLoaderWhenDataIsEmpty,
    skip,
    ssr,
    passErrors,
    fetchPolicy,
    readyFetchPolicy,
    notifyOnNetworkStatusChange,
    pollInterval,
    setLoadingOnPoll,
    ...rest
  }: {
    query: DocumentNode;
    variables?: V;
    context?: Context;
    ssr?: boolean;
    passErrors?: string[];
    fetchPolicy?: WatchQueryFetchPolicy;
    readyFetchPolicy?: WatchQueryFetchPolicy;
    notifyOnNetworkStatusChange?: boolean;
    pollInterval?: number;
    setLoadingOnPoll?: boolean;
    showLoader?: boolean;
    skip?: boolean;
    errorMessage: string;
    loader?: ReactElement | null;
    showLoaderWhenSetVariables?: boolean;
    showLoaderWhenDataIsEmpty?: boolean;
    [rest: string]: unknown;
  }) => {
    const variablesValue = variables ?? ({} as V);
    const passErrorsValue = passErrors ?? [];
    const showLoaderWhenSetVariablesValue = showLoaderWhenSetVariables ?? true;
    const skipValue = skip ?? false;
    const [currentFetchPolicy, setCurrentFetchPolicy] = useState(
      fetchPolicy ?? 'cache-and-network',
    );
    const {
      loading: clientLoading,
      error,
      data,
      previousData,
      fetchMore,
      networkStatus,
      refetch,
    } = useQuery<Q, V>(query, {
      variables: variablesValue,
      context: { ...context, passErrors: passErrorsValue },
      ssr: ssr ?? true,
      fetchPolicy: currentFetchPolicy,
      notifyOnNetworkStatusChange: notifyOnNetworkStatusChange ?? true,
      pollInterval,
      skip: skipValue,
    });

    useEffect(() => {
      if (readyFetchPolicy && networkStatus === NetworkStatus.ready) {
        setCurrentFetchPolicy(readyFetchPolicy);
      }
    }, [readyFetchPolicy, networkStatus]);

    const loading = getLoading(
      clientLoading,
      setLoadingOnPoll ?? false,
      networkStatus,
    );
    const intl = useIntl();

    const hasError = error
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      && (!error.graphQLErrors || isEmpty(
        error.graphQLErrors
          .filter(({ message }) => passErrorsValue.includes(message)),
      ));

    const currentData = showLoaderWhenSetVariablesValue
      ? data : data ?? previousData;

    // if there was a change in the variables (in default case) or
    // it's loading and there is no data yet
    const showLoader = (showLoaderWhenSetVariablesValue
      && networkStatus === NetworkStatus.setVariables)
    || (loading && isEmpty(currentData))
    || ((showLoaderWhenDataIsEmpty ?? false) && isEmpty(currentData))
    || skipValue;

    return (
      <>
        {showLoader && (loader ?? <Loader />)}
        {hasError && (
        <ErrorWrapper>
          <Error error={errorMessage} />
          <Button
            startIcon={<ReplayIcon />}
            onClick={() => refetch()}
          >
            {intl.formatMessage(messages.retryLabel)}
          </Button>
        </ErrorWrapper>
        )}
        {((!showLoader && !hasError) || renderAlways || !isEmpty(data)) && (
        <Component
          data={
            currentData as typeof renderAlways extends true ? Q | undefined : Q
          }
          loading={loading}
          fetchMore={fetchMore}
          variables={variablesValue}
          showLoaderWhenSetVariables={showLoaderWhenSetVariablesValue}
          error={error}
          showLoader={showLoader}
          refetch={refetch}
          skip={skipValue}
          {...rest as P}
        />
        )}
      </>
    );
  };

  WrappedComponent.propTypes = wrappedComponentPropertyTypes;

  return WrappedComponent;
};

export default withQuery;
